import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Mail, Phone, Send, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_INFO } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", preferred_date: "", preferred_time: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", message: "", preferred_date: "", preferred_time: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const info = [
    { icon: MapPin, label: CONTACT_INFO.address },
    { icon: Mail, label: CONTACT_INFO.email },
    { icon: Phone, label: CONTACT_INFO.phone },
  ];

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden" data-testid="contact-section">
      <div className="orb w-80 h-80 bg-[#00F0FF]/15 -left-20 bottom-0" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-xs tracking-[0.2em] uppercase font-mono text-cyan mb-4">{t("contact_eyebrow")}</div>
          <h2 className="font-head font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">{t("contact_title")}</h2>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-md">{t("contact_subtitle")}</p>

          <div className="mt-10 space-y-5">
            {info.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="flex items-center gap-4" data-testid={`contact-info-${i}`}>
                  <span className="w-11 h-11 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-cyan" />
                  </span>
                  <span className="text-zinc-300">{it.label}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-8 font-mono text-sm text-zinc-600">{CONTACT_INFO.company} · {t("footer_offices")}</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass rounded-2xl border border-white/10 p-8"
        >
          {status === "success" ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="contact-success">
              <CheckCircle2 size={56} className="text-cyan mb-6" />
              <p className="text-lg text-white font-head">{t("form_success")}</p>
              <button onClick={() => setStatus("idle")} className="mt-8 btn-ghost px-6 py-3 rounded-full text-sm font-mono text-white" data-testid="contact-reset">
                ↺
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={t("form_name")} testid="form-name">
                  <input required value={form.name} onChange={upd("name")} data-testid="input-name" className="input-neon" />
                </Field>
                <Field label={t("form_email")} testid="form-email">
                  <input required type="email" value={form.email} onChange={upd("email")} data-testid="input-email" className="input-neon" />
                </Field>
              </div>
              <Field label={t("form_company")} testid="form-company">
                <input value={form.company} onChange={upd("company")} data-testid="input-company" className="input-neon" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={t("form_date")} testid="form-date">
                  <input type="date" value={form.preferred_date} onChange={upd("preferred_date")} data-testid="input-date" className="input-neon" />
                </Field>
                <Field label={t("form_time")} testid="form-time">
                  <input type="time" value={form.preferred_time} onChange={upd("preferred_time")} data-testid="input-time" className="input-neon" />
                </Field>
              </div>
              <Field label={t("form_message")} testid="form-message">
                <textarea required rows={4} value={form.message} onChange={upd("message")} data-testid="input-message" className="input-neon resize-none" />
              </Field>
              {status === "error" && <p className="text-sm text-[#FF0055] font-mono" data-testid="contact-error">{t("form_error")}</p>}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="contact-submit"
                className="btn-neon w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold uppercase tracking-wide font-mono text-sm disabled:opacity-60"
              >
                {status === "sending" ? t("form_sending") : t("form_submit")}
                {status !== "sending" && <Send size={16} />}
              </button>
            </form>
          )}
        </motion.div>
      </div>

      <style>{`
        .input-neon {
          width: 100%;
          background: rgba(0,240,255,0.04);
          border: 1px solid rgba(0,240,255,0.45);
          border-radius: 12px;
          padding: 12px 14px;
          color: #fff;
          font-size: 15px;
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
        }
        .input-neon:hover { border-color: rgba(0,240,255,0.7); }
        .input-neon:focus { border-color: #00F0FF; box-shadow: 0 0 0 3px rgba(0,240,255,0.18); background: rgba(0,240,255,0.06); }
        .input-neon::placeholder { color: #52525b; }
      `}</style>
    </section>
  );
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-mono uppercase tracking-wide text-[#00F0FF] mb-2">{label}</span>
    {children}
  </label>
);
